import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import { formatCents } from '@shared/calculations/pricing.ts';
import { Checkbox } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import { getDateLocale } from '../../i18n/locale.ts';
import { useSession } from '../../state/session.tsx';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * Whether canceling a visit inside the notice window can charge a fee.
 * Off until switched on. The hours and the amount are edited either way, so
 * turning it on starts from a policy the office has already looked at.
 */

const snapshot = (company: Company) => ({
  lateCancelFeeEnabled: company.lateCancelFeeEnabled,
  lateCancelNoticeHours: company.lateCancelNoticeHours,
  lateCancelFeeCents: company.lateCancelFeeCents,
});

const toPayload = (draft: ReturnType<typeof snapshot>) => ({
  lateCancelFeeEnabled: draft.lateCancelFeeEnabled,
  lateCancelNoticeHours: draft.lateCancelNoticeHours,
  lateCancelFeeCents: draft.lateCancelFeeCents,
});

export default function LateCancelCard() {
  const { t } = useTranslation();
  const { company } = useSession();
  const form = useCompanyCardForm(snapshot, toPayload);
  const draft = form.draft;
  if (!draft) return null;

  const currency = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const amount = formatCents(draft.lateCancelFeeCents, currency, getDateLocale());

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="late-cancel-heading">
      <div className="work-section-heading">
        <h2 id="late-cancel-heading" className="mb-4 text-lg font-semibold">
          {t('settings.lateCancelHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.lateCancelDescription')}</p>
      <Checkbox
        label={t('settings.lateCancelEnable')}
        hint={t('settings.lateCancelEnableHint')}
        checked={draft.lateCancelFeeEnabled}
        disabled={form.readOnly}
        onChange={(event) => form.set('lateCancelFeeEnabled', event.target.checked)}
      />
      <div className="grid gap-x-4 sm:grid-cols-2">
        <NumberInput
          label={t('settings.lateCancelNoticeHours')}
          min={1}
          max={336}
          value={draft.lateCancelNoticeHours}
          disabled={form.readOnly}
          error={form.fieldErrors.lateCancelNoticeHours}
          onValueChange={(hours) => form.set('lateCancelNoticeHours', hours ?? 1)}
        />
        <MoneyInput
          label={t('settings.lateCancelFee')}
          valueCents={draft.lateCancelFeeCents}
          currencyCode={currency}
          disabled={form.readOnly}
          error={form.fieldErrors.lateCancelFeeCents}
          onChangeCents={(cents) => form.set('lateCancelFeeCents', cents ?? 0)}
        />
      </div>
      {draft.lateCancelFeeEnabled && draft.lateCancelFeeCents > 0 ? (
        <p className="mb-4 text-sm">
          {t('settings.lateCancelSummary', { count: draft.lateCancelNoticeHours, amount })}
        </p>
      ) : (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.lateCancelOff')}</p>
      )}
      <CardSaveFooter form={form} saveLabel={t('settings.saveLateCancel')} />
    </section>
  );
}
